// Coaches catalogue — single source of truth.
//
// Adding a new coach = append one CoachRecord to COACHES + drop the
// photo into public/coaches/. No code or markup changes.
//
// The shape mirrors the brief at CC_brief_coaches_section.md §1:
// flat record per coach, i18n fields keyed by language code.

export type Lang = "ru" | "az" | "en";

export type I18nString = Record<Lang, string>;
export type I18nStringArray = Record<Lang, string[]>;

export type CoachRecord = {
  /** Slug — also drives the photo filename / future profile URL. */
  id: string;
  /** Path under /public for the portrait (3:4 or 4:5 crop recommended). */
  photo: string;
  /** Listing order; lower comes first. */
  order: number;

  // ── i18n fields ───────────────────────────────────────────────────
  name: I18nString;
  /** Role line — short, e.g. "Карьерный консультант · ICF-коуч". */
  role: I18nString;
  /** Short card tagline (1 sentence). */
  tagline: I18nString;
  /** Pill badge above the card, e.g. "12+ лет · 13–18". */
  experienceBadge: I18nString;
  /**
   * Consultation languages, displayed as small badges. Stored as
   * uppercase 2-letter codes ("RU", "AZ", "EN"). Same list per locale
   * because language codes are universal; kept as I18nStringArray to
   * stay symmetric with the rest of the schema.
   */
  languages: I18nStringArray;

  bio: I18nString;
  approach: I18nString;
  credentials: I18nStringArray;
  quote: I18nString;
  /** Alt text for the portrait (localised). */
  photoAlt: I18nString;
};

export const COACHES: CoachRecord[] = [
  {
    id: "nazilya-javanshir",
    photo: "/coaches/coach-nazilya-javanshir.jpg",
    order: 1,

    name: {
      ru: "Назиля Джаваншир",
      az: "Nazilə Cavanşir",
      en: "Nazilya Javanshir",
    },
    role: {
      ru: "Карьерный консультант · Профориентолог · ICF-коуч",
      az: "Karyera məsləhətçisi · Peşəyönümü mütəxəssisi · ICF kouçu",
      en: "Career consultant · Career-guidance specialist · ICF coach",
    },
    tagline: {
      ru: "Помогаю подросткам раскрыть потенциал, понять свои сильные стороны и превратить результаты профориентации в осознанный выбор будущего.",
      az: "Yeniyetmələrə potensiallarını üzə çıxarmağa, güclü tərəflərini anlamağa və peşəyönümü nəticələrini gələcəyə dair şüurlu seçimə çevirməyə kömək edirəm.",
      en: "I help teenagers uncover their potential, understand their strengths, and turn career-assessment results into a conscious choice for their future.",
    },
    experienceBadge: {
      ru: "12+ лет · профориентация подростков 13–18",
      az: "12+ il · 13–18 yaşlı yeniyetmələrin peşəyönümü",
      en: "12+ years · career guidance for teens 13–18",
    },
    // Consultation languages — confirmed by founder (Coach profile v2,
    // 2026-06-11): AZ and RU. Order chosen so AZ leads the badge row
    // for the primary-jurisdiction audience.
    languages: {
      ru: ["AZ", "RU"],
      az: ["AZ", "RU"],
      en: ["AZ", "RU"],
    },

    bio: {
      ru: "Более 12 лет работаю с детьми, подростками и семьями. Специализируюсь на профориентации подростков 13–18 лет, развитии эмоционального интеллекта и сопровождении профессионального самоопределения.",
      az: "12 ildən artıqdır uşaqlar, yeniyetmələr və ailələrlə işləyirəm. 13–18 yaşlı yeniyetmələrin peşəyönümü, emosional intellektin inkişafı və peşə özünümüəyyənləşməsinin müşayiəti üzrə ixtisaslaşmışam.",
      en: "For over 12 years I have worked with children, teenagers and families. I specialize in career guidance for teenagers aged 13–18, developing emotional intelligence, and supporting the process of professional self-determination.",
    },
    approach: {
      ru: "Я не выбираю профессию за подростка и не даю готовых ответов. Моя задача — помочь ему лучше понять себя, увидеть свои возможности и найти направление, которое соответствует его интересам, способностям и ценностям. Решение всегда остаётся за подростком и его семьёй.",
      az: "Mən yeniyetmə əvəzinə peşə seçmirəm və hazır cavablar vermirəm. Məqsədim — ona özünü daha yaxşı anlamağa, imkanlarını görməyə və maraqlarına, qabiliyyətlərinə və dəyərlərinə uyğun istiqamət tapmağa kömək etməkdir. Qərar həmişə yeniyetmə və onun ailəsinə aiddir.",
      en: "I don't choose a profession for the teenager or hand out ready answers. My role is to help them understand themselves better, see their options, and find a direction that matches their interests, abilities and values. The decision always stays with the teenager and their family.",
    },
    credentials: {
      ru: [
        "Магистр психологии (РАНХиГС, Санкт-Петербург)",
        "Сертифицированный коуч Erickson International (ICF)",
        "Эксперт по развитию эмоционального интеллекта",
        "Автор магистерского исследования о связи эмоционального интеллекта и готовности старшеклассников к профессиональному выбору",
      ],
      az: [
        "Psixologiya magistri (RANEPA, Sankt-Peterburq)",
        "Sertifikatlı kouç, Erickson International (ICF)",
        "Emosional intellektin inkişafı üzrə ekspert",
        "Yuxarı sinif şagirdlərində emosional intellekt və peşə seçiminə hazırlıq arasındakı əlaqə üzrə magistr tədqiqatının müəllifi",
      ],
      en: [
        "Master of Psychology (RANEPA, St. Petersburg)",
        "Certified coach, Erickson International (ICF)",
        "Expert in emotional-intelligence development",
        "Author of a master's study on the link between emotional intelligence and high-schoolers' readiness for career choice",
      ],
    },
    quote: {
      ru: "Осознанный выбор начинается не с профессии, а с понимания себя.",
      az: "Şüurlu seçim peşədən deyil, özünü anlamaqdan başlayır.",
      en: "A conscious choice begins not with a profession, but with understanding yourself.",
    },
    photoAlt: {
      ru: "Назиля Джаваншир — карьерный коуч Yolutap",
      az: "Nazilə Cavanşir — Yolutap karyera kouçu",
      en: "Nazilya Javanshir — Yolutap career coach",
    },
  },
  {
    id: "yegana-seyidli-tufek",
    photo: "/coaches/coach-yegana-seyidli-tufek.jpg",
    order: 2,

    name: {
      ru: "Егана Сеидли Туфек",
      az: "Yeganə Seyidli Tüfək",
      en: "Yegana Seyidli Tufek",
    },
    role: {
      ru: "Профориентолог · Карьерный консультант",
      az: "Peşəyönümü mütəxəssisi · Karyera məsləhətçisi",
      en: "Career-guidance specialist · Career counselor",
    },
    tagline: {
      ru: "Помогаю найти лучшее применение вашим интересам и талантам — в учёбе и карьере.",
      az: "Maraq və istedadlarınızı gerçəkləşdirməyin ən uyğun yolunu tapmağa kömək edirəm — təhsildə və karyerada.",
      en: "I help you find the best fit for your interests and talents — in education and career.",
    },
    experienceBadge: {
      ru: "15 лет в HR · 3 года в профориентации",
      az: "HR-da 15 il · peşəyönümündə 3 il",
      en: "15 years in HR · 3 years in career guidance",
    },
    // Consultation languages per coach profile (2026-09-10): AZ, RU, EN.
    languages: {
      ru: ["AZ", "RU", "EN"],
      az: ["AZ", "RU", "EN"],
      en: ["AZ", "RU", "EN"],
    },

    bio: {
      ru: "15 лет в сфере HR и управления талантами, последние 3 года — в карьерном консультировании. Работаю с подростками (12+) и со взрослыми.",
      az: "15 il HR və istedadların idarə edilməsi, son 3 il isə peşəyönümü və karyera məsləhəti. Çalışdığım qruplar: yeniyetmələr (12+) və böyüklər.",
      en: "15 years in HR and Talent Management; for the last 3 years, career counseling and development. I work with both teenagers (12+) and adults.",
    },
    approach: {
      ru: "Каждый имеет право на лучшую версию себя. Я помогаю найти путь к самореализации через осознанный выбор карьеры.",
      az: "Hər kəs özünün ən yaxşı versiyasını yaratmaq haqqına sahibdir. Mən şüurlu karyera seçimi vasitəsilə özünü reallaşdırmaq yolunu tapmağa kömək edirəm.",
      en: "Everyone deserves to become the best version of themselves. I help people find their path to self-realization through conscious career choices.",
    },
    credentials: {
      ru: [
        "Филологический факультет Бакинского государственного университета",
        "MBA, CEU Business School",
        "Сертифицированный консультант по профориентации и карьерному консультированию подростков и взрослых",
        "Инструменты и методики: профориентационные и личностные тесты (Холланд, Big Five, Шейн, Климов, VIA и др.), карьерные нарративы (теория конструирования карьеры М. Савикаса), проективные методики, рефлексивные упражнения",
      ],
      az: [
        "Bakı Dövlət Universiteti, Filologiya fakültəsi",
        "CEU Business School, Biznesin idarə edilməsi (MBA)",
        "Peşəyönümü və karyera məsləhətçiliyi üzrə sertifikatlı məsləhətçi",
        "Alət və metodikalar: peşəyönümü və şəxsiyyət testləri (Holland, Big Five, Şeyn, Klimov, VIA və s.), karyera narrativləri (Savikasın karyera konstruktivizmi modeli), proyektiv metodikalar, refleksiya çalışmaları",
      ],
      en: [
        "Baku State University, Faculty of Philology",
        "MBA, CEU Business School",
        "Certified career guidance and career counseling specialist for adolescents and adults",
        "Tools and methods: career guidance and personality assessments (Holland, Big Five, Schein, Klimov, VIA, etc.), career narratives (Savickas' Career Construction Theory), projective techniques, reflective exercises",
      ],
    },
    quote: {
      ru: "Совершенство — враг хорошего.",
      az: "Mükəmməl yaxşının düşmənidir.",
      en: "Perfect is the enemy of good.",
    },
    photoAlt: {
      ru: "Егана Сеидли Туфек — карьерный консультант Yolutap",
      az: "Yeganə Seyidli Tüfək — Yolutap karyera məsləhətçisi",
      en: "Yegana Seyidli Tufek — Yolutap career counselor",
    },
  },
];

// ── UI strings for the coaches page (per-locale) ─────────────────────
// Centralised here so future content edits stay in one place.

export const COACHES_PAGE_STRINGS = {
  pageTitle: {
    ru: "Коучи Yolutap",
    az: "Yolutap kouçları",
    en: "Yolutap coaches",
  },
  pageIntro: {
    ru: "Сертифицированные специалисты, которые помогают подростку перевести результаты диагностики в осознанные шаги к будущему.",
    az: "Yeniyetməyə diaqnostika nəticələrini gələcəyə doğru şüurlu addımlara çevirməyə kömək edən sertifikatlı mütəxəssislər.",
    en: "Certified specialists who help the teenager turn diagnostic results into conscious steps toward the future.",
  },
  languagesLabel: {
    ru: "Языки консультаций",
    az: "Məsləhət dilləri",
    en: "Consultation languages",
  },
  experienceLabel: {
    ru: "Опыт",
    az: "Təcrübə",
    en: "Experience",
  },
  toggleMore: {
    ru: "Подробнее",
    az: "Ətraflı",
    en: "Read more",
  },
  toggleLess: {
    ru: "Свернуть",
    az: "Bağla",
    en: "Show less",
  },
  bioHeading: {
    ru: "О коуче",
    az: "Kouç haqqında",
    en: "About the coach",
  },
  approachHeading: {
    ru: "Подход",
    az: "Yanaşma",
    en: "Approach",
  },
  credentialsHeading: {
    ru: "Квалификация",
    az: "İxtisas və sertifikatlar",
    en: "Credentials",
  },
  cta: {
    ru: "Записаться на сессию",
    az: "Sessiyaya yazıl",
    en: "Book a session",
  },
  meetTheCoach: {
    ru: "Познакомьтесь с коучем",
    az: "Kouçla tanış olun",
    en: "Meet the coach",
  },
} as const;
